import { useEffect, useState } from "react";

export type GeoState =
  | { status: "idle" }
  | { status: "prompting" }
  | { status: "granted"; lat: number; lng: number }
  | { status: "denied"; reason: string }
  | { status: "unavailable" };

const KEY = "what.geo.consent";

export function useGeolocation(autoRequest = false) {
  const [state, setState] = useState<GeoState>({ status: "idle" });

  const request = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({ status: "unavailable" });
      return;
    }
    setState({ status: "prompting" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try { localStorage.setItem(KEY, "granted"); } catch {}
        setState({ status: "granted", lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        try { localStorage.setItem(KEY, "denied"); } catch {}
        setState({ status: "denied", reason: err.message });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  useEffect(() => {
    if (!autoRequest) return;
    const tryStored = () => {
      try {
        if (localStorage.getItem(KEY) === "granted") request();
      } catch {}
    };
    tryStored();
    // The location permission prompt fires this once the user grants access,
    // so lists mounted before the prompt can re-read their position without
    // waiting for a navigation.
    const onGranted = () => tryStored();
    window.addEventListener("what:geo-granted", onGranted);
    return () => window.removeEventListener("what:geo-granted", onGranted);
  }, [autoRequest]);


  return { state, request };
}

// Haversine distance in kilometers
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function formatDistance(km: number) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}
